import { useState } from "react";

import { isImageMimeType } from "@blog/content-schema";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@blog/ui";

import { t } from "../lib/i18n";
import { useApi } from "../app/ApiContext";
import { PageHeader } from "../app/Shell";
import { formatBytes, formatDate } from "../lib/format";
import { useAsync, useSite } from "../lib/hooks";

/**
 * Media library (TASK-804, SPEC §56).
 *
 * Browse, search, copy a URL, delete. **Uploading lives in the editor**, not
 * here: media is stored under `assets/posts/{entryId}/` (SPEC §17), and this
 * page has no entry context to put a file under. An upload button here would
 * have to invent one.
 */
export function MediaLibrary() {
  const client = useApi();
  const { site, loading: siteLoading } = useSite(client);

  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  const siteId = site?.id;

  const data = useAsync(async () => {
    if (siteId === undefined) {
      return null;
    }

    return client.listMedia(siteId, search === "" ? {} : { search });
  }, [siteId, search]);

  async function copy(url: string): Promise<void> {
    await navigator.clipboard.writeText(url);
    setCopied(url);
    window.setTimeout(() => {
      setCopied(null);
    }, 1500);
  }

  async function remove(mediaId: string): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    await client.deleteMedia(siteId, mediaId);
    data.reload();
  }

  if (siteLoading || data.loading) {
    return <p className="text-sm text-neutral-500">{t("common.loading")}</p>;
  }

  const media = data.data ?? [];

  return (
    <>
      <PageHeader
        title={t("media.title")}
        description={t("media.countDesc", { count: media.length })}
      />

      <Input
        className="mb-4 max-w-xs"
        placeholder={t("media.search")}
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
        }}
      />

      {data.error === null ? null : (
        <p className="mb-4 text-sm text-red-600">{data.error}</p>
      )}

      {media.length === 0 ? (
        <EmptyState
          message={search === "" ? t("media.noMedia") : t("media.noMatch")}
          hint={t("media.emptyHint")}
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("media.thPreview")}</TableHead>
                <TableHead>{t("media.thFilename")}</TableHead>
                <TableHead>{t("media.thType")}</TableHead>
                <TableHead>{t("media.thSize")}</TableHead>
                <TableHead>{t("media.thUploaded")}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {media.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {/* Lazy so a library of a hundred images does not fetch all
                        of them to render a list. Attachments have no thumbnail:
                        the extension stands in for one. */}
                    {isImageMimeType(item.mimeType) ? (
                      <img
                        src={item.publicUrl}
                        alt={item.filename}
                        loading="lazy"
                        className="h-10 w-10 rounded border border-neutral-200 object-cover"
                      />
                    ) : (
                      <span className="flex h-10 w-10 items-center justify-center rounded border border-neutral-200 text-xs font-medium uppercase text-neutral-500">
                        {item.filename.includes(".")
                          ? (item.filename.split(".").pop() ?? "?").slice(0, 4)
                          : "?"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-medium text-neutral-900">{item.filename}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{item.mimeType}</Badge>
                  </TableCell>
                  <TableCell className="text-neutral-500">{formatBytes(item.size)}</TableCell>
                  <TableCell className="text-neutral-500">{formatDate(item.createdAt)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void copy(item.publicUrl)}
                      >
                        {copied === item.publicUrl ? t("media.copied") : t("media.copyUrl")}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => void remove(item.id)}
                      >
                        {t("media.delete")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </>
  );
}
